import {TOKEN_GLINT} from '../../configs/tokenGlintAnimation';

/**
 * Расчеты бликов на жетонах (см. configs/tokenGlintAnimation): когда блеснуть
 * и как блик бежит по жетону. Здесь только числа — спрайтами владеет
 * scenes/TokenGlints. style — вид блика своего материала (TOKEN_GLINT_STYLES)
 */

/**
 * Промежуток до следующего блика жетона: средний с разбросом в обе стороны
 * @param {function(): number} rng — источник случайности, от 0 до 1
 * @param {{averageIntervalMs: number, intervalSpread: number}} style
 * @returns {number} мс
 */
export const resolveGlintIntervalMs = (rng, style) => {
  const spread = Math.min(Math.max(style.intervalSpread, 0), 1);
  return style.averageIntervalMs * (1 - spread + 2 * spread * rng());
};

/**
 * Сколько ждать первого блика жетона, только что появившегося на поле:
 * случайная точка внутри случайного промежутка. Так жетоны с первой же минуты
 * идут вразнобой, а не светятся строем через одинаковое время после старта
 * @returns {number} мс
 */
export const resolveFirstGlintDelayMs = (rng, style) => rng() * resolveGlintIntervalMs(rng, style);

/**
 * Контур жетона — шестиугольник с вершинами слева и справа, вписанный в
 * картинку жетона (у жетонов ключевых точек картинка занимает его целиком)
 * @param {{x: number, y: number, width: number, height: number}} token — центр и размер жетона на поле
 * @param {{maskInsetShare: number}} [config]
 * @returns {Array<{x: number, y: number}>}
 */
export const buildGlintMaskPoints = ({x, y, width, height}, config = TOKEN_GLINT) => {
  const halfWidth = (width / 2) * (1 - config.maskInsetShare);
  const halfHeight = (height / 2) * (1 - config.maskInsetShare);

  return [
    {x: x - halfWidth, y},
    {x: x - halfWidth / 2, y: y - halfHeight},
    {x: x + halfWidth / 2, y: y - halfHeight},
    {x: x + halfWidth, y},
    {x: x + halfWidth / 2, y: y + halfHeight},
    {x: x - halfWidth / 2, y: y + halfHeight},
  ];
};

/**
 * Путь блика через жетон: наклонная полоса входит слева из-за края жетона и
 * уходит за правый край — целиком, чтобы блик не возникал и не обрывался
 * посреди жетона
 * @param {{x: number, y: number, width: number, height: number}} token
 * @param {{angleDeg: number, bandWidthShare: number}} style
 * @returns {{startX: number, endX: number, y: number, bandWidth: number, bandHeight: number, angleDeg: number}}
 */
export const resolveGlintBand = ({x, y, width, height}, style) => {
  const angle = (style.angleDeg * Math.PI) / 180;
  const bandWidth = width * style.bandWidthShare;
  // Наклоненная полоса обязана перекрыть жетон по всей высоте
  const bandHeight = (height + bandWidth * Math.abs(Math.sin(angle))) / Math.cos(angle);
  // Полуширина наклоненной полосы по горизонтали
  const bandReach = (bandWidth / 2) * Math.cos(angle) + (bandHeight / 2) * Math.abs(Math.sin(angle));

  return {
    startX: x - width / 2 - bandReach,
    endX: x + width / 2 + bandReach,
    y,
    bandWidth,
    bandHeight,
    angleDeg: style.angleDeg,
  };
};

/**
 * Где блик на своем пути: плавно разгоняется и плавно уходит
 * @param {{startX: number, endX: number}} band
 * @param {number} progress — доля пройденного времени, от 0 до 1
 * @returns {number}
 */
export const resolveGlintBandX = ({startX, endX}, progress) => {
  const clamped = Math.min(Math.max(progress, 0), 1);
  const eased = 0.5 - Math.cos(Math.PI * clamped) / 2;
  return startX + (endX - startX) * eased;
};
